import {FormControl} from "@angular/forms";
import {TranslateService} from "@ngx-translate/core";
import DOMPurify from "dompurify";
import {getTimeRemaining, MESSAGES_ALLOWED_TAGS, sanitizeText} from "shared/utils";
import {ClientUser, RolesUtils, Sex, User} from "shared/user";
import {getMessageIdByError} from "shared/errors";
import {LANGUAGE_RU} from "shared/language";
import {UsersListRoomInfo} from "../ui/chat-module/components/users/users-list/users-list.component";
import {environment} from "../../environments/environment";
import {ChatVersions} from "../../../shared/chat.versions";
import {RoomType} from "../../../shared/room";
import {ChatInfo} from "../models/chat";
import {RoomClientUsersWrapper} from "../models/client.user";

export function homePagesBackgroundImageClass() {
    return environment.chatVersion == ChatVersions.VT ? 'main-container_vc' : 'main-container_sx'
}

export function hasAlternateLanguages(): boolean {
    return false//environment.chatVersion == ChatVersions.VT
}

export function isInBrowser(): boolean {
    return !!(typeof window !== 'undefined' && window.document);
}

export function urlForLanguage(language: string) {
    if (!isInBrowser()) return '';
    const {protocol, pathname} = window.location;

    const host = mainHost()

    let subdomain = ''

    if (language == LANGUAGE_RU) {
        subdomain = 'ru1' + '.'
    }

    return `${protocol}//${subdomain}${host}${pathname}`;
}

export function mainHost() {
    if (!isInBrowser()) return '';
    const split = window.location.host.split('.')

    if (split.length > 2) {
        return `${split[1]}.${split[2]}`
    } else {
        return window.location.host
    }
}

export function goToSiteForLanguage(translate: TranslateService, language: string) {
    if (isInBrowser()) {
        window.open(urlForLanguage(language), '_self');
    }
}

export function isTouchScreen() {
    if (!isInBrowser()) return false;
    try {
        document.createEvent('TouchEvent');
        return true;
    } catch (e) {
        return false;
    }
}

export function isUserLocalModerator(room: ChatInfo, user: string): boolean {
    if (!room) {
        return false
    }

    if (room.type != RoomType.PAID_ROOM) {
        return false
    }

    if (room.createdBy == user) {
        return true
    }

    return !!room.moderators.find((value) => {
        return value.login == user
    });
}

export function maxRoomMessages(title: string) {
    let maxMessagesCount = isTouchScreen() ? 50 : 100;

    //Это тупо, но ничего лучше я не смог придумать что не передавать дополнительные параметры
    if (title === 'Блокировки' || title === 'Blocks list') {
        maxMessagesCount = 500
    }

    return maxMessagesCount
}

export function errorMessageForCantEnterBySex(translateService: TranslateService, userSex: Sex): string {
    let genderTitleKey: string

    switch (userSex) {
        case Sex.female:
            genderTitleKey = 'cant_enter_gender_girls'
            break;
        case Sex.male:
            genderTitleKey = 'cant_enter_gender_boys'
            break;
        case Sex.trans:
            genderTitleKey = 'cant_enter_gender_trans'
            break;

    }

    const genderTitle = translateService.instant(genderTitleKey)

    return translateService.instant('gender_cant_enter_to_room', {'gender': genderTitle})
}

export function clientSanitizeText(text: string) {
    const preparedText = DOMPurify.sanitize(text, {ALLOWED_TAGS: MESSAGES_ALLOWED_TAGS})
    return sanitizeText(preparedText)
}

export function isVorona(user: User): boolean {
    return user.login == "Ворона"
}

export function isPremiumActive(user: User): boolean {
    return !!(user && user.premiumUntil && Number(user.premiumUntil) > Date.now());
}

export function getPasswordErrorTextId(formControl: FormControl) {
    if (formControl.hasError('required')) {
        return 'fill_in_a_password'
    } else if (formControl.hasError('wrongPassword')) {
        return 'wrong_password'
    } else if (formControl.hasError('customValidatorError')) {
        return getMessageIdByError(formControl.getError('customValidatorError').error)
    }
    return ""
}

export function getSexErrorText(formControl: FormControl) {
    if (formControl.hasError('required')) {
        return 'choose_a_gender'
    } else if (formControl.hasError('customValidatorError')) {
        return getMessageIdByError(formControl.getError('customValidatorError').error)
    }
    return ""
}

export function prepareBlocksTimeList(translate: TranslateService): Array<{ time: number, title: string }> {
    const hoursIntervals = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 36, 48, 72,]

    const blocksTimeList = new Array<{ time: number, title: string }>()

    hoursIntervals.forEach((value) => {
        //TODO: Если время больше 24 часов то дописывать сколько это суток
        blocksTimeList.push({time: value, title: `${value} ${pluralize(value, PluralType.HOURS, translate)}`})
    })

    return blocksTimeList
}

function getPluralizeTranslations(id: string, translate: TranslateService): Pluralize {
    return {
        zero: translate.instant(id + '.zero'),
        one: translate.instant(id + '.one'),
        two: translate.instant(id + '.two'),
        few: translate.instant(id + '.few'),
        many: translate.instant(id + '.many'),
        other: translate.instant(id + '.other'),
    }
}

export function pluralize(count: number, type: PluralType, translate: TranslateService): string {
    const language = translate.currentLang();

    const pluralize = getPluralizeTranslations(type, translate)

    const rule = new Intl.PluralRules(language).select(count)

    let result = pluralize['other']

    if (pluralize[rule]) {
        result = pluralize[rule]
    }

    return result
}

/**
 * Форматирует относительное время
 * @param translate
 * @param timeTo время в секундах
 * @param includeMinutes true если нужно добавить минуты
 * @param includeSeconds true если нужно включить секунды
 */
export function formatTime(translate: TranslateService, timeTo: number, includeMinutes: boolean = true, includeSeconds: boolean = false): string {
    const data = getTimeRemaining(timeTo)

    let result = ''

    if (data.days > 0) {
        result += `${data.days} ${pluralize(data.days, PluralType.DAYS, translate)} `
    }

    if (data.hours > 0) {
        result += `${data.hours} ${pluralize(data.hours, PluralType.HOURS, translate)} `
    }

    if (includeMinutes && data.minutes > 0) {
        result += `${data.minutes} ${pluralize(data.minutes, PluralType.MINUTES, translate)} `
    }

    if (includeSeconds && data.seconds > 0) {
        result += `${data.seconds} ${pluralize(data.seconds, PluralType.SECONDS, translate)} `
    }

    return result
}

export type Pluralize = {
    zero?: string,
    one?: string,
    two?: string,
    few?: string,
    many?: string,
    other: string
}

export enum PluralType {
    DAYS = 'days',
    HOURS = 'hours',
    MINUTES = 'minutes',
    SECONDS = 'seconds',
    YEARS = 'years'
}